/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {SpreaderStatTile, SpreaderStatTileProps} from './SpreaderStatTile';

export type SpreaderStatTileData = SpreaderStatTileProps;

export interface SpreaderStatTileRowProps {
  tiles: SpreaderStatTileData[];
  // sizes each tile to its content instead of an even share of the row ...
  fitContent?: boolean;
}

const useStyles = makeStyles(() => ({
  row : {
    display : 'flex',
    width   : '100%',
    gap     : '16px'
  }
}));

/**
 * SpreaderStatTileRow lays out the Spreader accordion's top stat tiles -
 * Force America Type 1's Granular Set Rate/Spread Rate Index/Spinner Rate,
 * Type 2's and Dickey-john's own tiles, Cirus/RoadWatch's - in a gapped
 * row, per the Figma "Diagnostic Tiles" frame. It renders nothing when
 * `tiles` is empty/undefined, rather than an empty gapped row.
 *
 * `fitContent` is passed through to every tile in the row (`SpreaderStatTile`'s
 * own prop of the same name) - a content-sized tile rather than an even
 * split of the row's width, used by Dickey-john's three top tiles via its
 * layout's own `tilesFitContent`.
 *
 * ```typescript
 *
 *   import {SpreaderStatTileRow} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderStatTileRow';
 *   ...
 *
 *   <SpreaderStatTileRow tiles={[
 *     {label: 'Granular Set Rate', value: 'N/A'},
 *     {label: 'Spread Rate Index', value: '0'},
 *     {label: 'Spinner Rate', value: '0'}
 *   ]} />
 *
 * ```
 *
 * @param   tiles      The row's tiles.
 * @param   fitContent Sizes every tile to its content when `true`.
 * @returns            The stat tile row, or nothing when `tiles` is empty.
 */
export const SpreaderStatTileRow = ({tiles, fitContent = false}: SpreaderStatTileRowProps) => {
  const classes = useStyles();
  let row: React.ReactElement = null;

  if (tiles?.length) {
    row = (
      <div className={classes.row}>
        {tiles.map(function(tile) {
          return (
            <SpreaderStatTile key={tile.label}
              label={tile.label}
              value={tile.value}
              unit={tile.unit}
              fitContent={fitContent} />
          );
        })}
      </div>
    );
  }

  return row;
};

export default memo(SpreaderStatTileRow);
